// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import { PART_TYPE_ASSEMBLY_ID } from '@evolvemep/api-ts';
import { Menu, Button, Flex } from '@mantine/core';
import { useReactFlow } from '@xyflow/react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import { useAssemblyEditor } from 'modules/Shop/CatalogSetup/PartEditor/useAssemblyEditor';
import { usePartEditor } from 'modules/Shop/CatalogSetup/PartEditor/usePartEditor';
import { useCreateNodesFromParts } from 'modules/Shop/CatalogSetup/PartEditor/utils';
import { type WriteInItemFields, EditItemPopover } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditItemPopover';
import {
  type PartToAdd,
  CatalogPartAdder,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/CatalogPartAdder';

export const AddPartToAssembly = ({ disabled }: { disabled?: boolean }) => {
  const { createNodesFromParts, createNodeFromWriteInItem } = useCreateNodesFromParts();
  const { publishing, part } = usePartEditor();
  const { addNode } = useAssemblyEditor();
  const { fitView, getViewport } = useReactFlow();

  const delayedFitView = (resolve: () => void) => {
    setTimeout(() => {
      resolve();
      void fitView({
        duration: 300,
        maxZoom: getViewport().zoom,
        padding: 0,
      });
    }, 250);
  };

  const addSelectedParts = async (parts: PartToAdd[]) =>
    new Promise<void>((resolve) => {
      createNodesFromParts(parts).map(addNode);
      delayedFitView(resolve);
    });

  const addWriteInPart = async (item: WriteInItemFields) =>
    new Promise<void>((resolve) => {
      addNode(createNodeFromWriteInItem(item));
      delayedFitView(resolve);
    });

  const [menuItemSelected, setMenuItemSelected] = useState<'add-write-in-item' | 'add-from-catalog' | null>(null);

  const [selectedCatalog, setSelectedCatalog] = useLocalStorage('SELECTED_CATALOG', null);

  return (
    <Flex>
      <Menu position="bottom-end">
        <Menu.Target>
          <Button size="sm" variant="light" disabled={publishing || disabled}>
            Add items
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<EvolveIcon icon="WriteInItem" />}
            onClick={() => setMenuItemSelected('add-write-in-item')}
          >
            Add write-in item
          </Menu.Item>
          <Menu.Item
            leftSection={<EvolveIcon icon="Catalog" />}
            onClick={() => setMenuItemSelected('add-from-catalog')}
          >
            Add from catalog
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <EditItemPopover
        opened={menuItemSelected === 'add-write-in-item'}
        onClose={() => setMenuItemSelected(null)}
        onSubmit={addWriteInPart}
      />

      <WrappedModal
        title="Add parts from catalog"
        opened={menuItemSelected === 'add-from-catalog'}
        onClose={() => setMenuItemSelected(null)}
        hideModalButtons
        headerRightComponent={
          <CatalogSelect onCatalogSelected={setSelectedCatalog} selectedCatalog={selectedCatalog} w={250} />
        }
        size="70rem"
        tall
        bodyProps={{
          flex: 1,
          p: 0,
        }}
      >
        <CatalogPartAdder
          partCatalog={selectedCatalog}
          onCatalogSelected={setSelectedCatalog}
          disablePart={(p) => p.partId === part?.partId || p.partTypeId === PART_TYPE_ASSEMBLY_ID}
          onAddParts={addSelectedParts}
          disabled={publishing}
        />
      </WrappedModal>
    </Flex>
  );
};
